import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOSchema from '@/components/feature/SEOSchema';

const milestones = [
  { year: '2019', title: '项目启动', description: '创始团队汇聚来自阿里、腾讯、字节跳动的顶级工程师，怀揣让开发平民化的愿景，正式启动无码项目。' },
  { year: '2020', title: 'Beta版发布', description: '经过18个月的潜心研发，无码平台Beta版本正式上线，首批100家企业用户参与内测。' },
  { year: '2021', title: '正式版发布', description: '无码1.0正式版发布，涵盖应用搭建、自动化流程、数据建模三大核心模块，注册用户突破10,000。' },
  { year: '2022', title: '融资与扩张', description: '完成A轮融资，团队扩展至200人，服务企业客户超过5,000家，覆盖制造、零售、教育等多个行业。' },
  { year: '2023', title: '产品矩阵完善', description: '推出企业版和私有化部署方案，上线微信小程序一键发布、跨端适配等重磅功能，用户突破50,000。' },
  { year: '2024', title: 'AI赋能', description: '率先将AI能力融入无码平台，推出智能表单识别、自然语言生成应用等AI辅助功能，引领行业创新。' },
  { year: '2025', title: '生态开放', description: '开放平台API和插件市场，构建繁荣的开发者生态。累计创建应用超过300万个，服务全球企业。' },
  { year: '2026', title: '迈向新征程', description: '用户突破100,000，成为国内领先的低代码/无码平台。持续深耕行业解决方案，助力千行百业数字化转型。' },
];

const team = [
  { name: '王明远', role: '创始人 & CEO', bio: '前阿里巴巴高级技术总监，15年企业级软件研发经验，致力于让软件开发变得更简单。' },
  { name: '陈思涵', role: '联合创始人 & CTO', bio: '前字节跳动技术专家，全栈工程师，主导无码平台核心架构设计。' },
  { name: '林小雅', role: '产品副总裁', bio: '前腾讯高级产品经理，10年B端产品经验，深谙企业数字化转型痛点。' },
  { name: '张磊', role: '技术副总裁', bio: '前美团技术总监，分布式系统专家，负责无码平台的性能优化和稳定性保障。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 无码开发平台 | 品牌介绍与联系方式';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const phoneAlt = formData.get('phone_alt') as string;
    if (phoneAlt && phoneAlt.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快与您联系。');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u4k5f8l97n3eas3hb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！我们会尽快与您联系。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <SEOSchema
        pageType="about"
        pageTitle="关于我们 - 无码开发平台"
        pageDescription="了解无码平台的品牌故事、发展历程和核心团队。无码是新一代低代码/无码开发平台，致力于让每个人都能创造数字化价值。"
        canonicalPath="/about"
        breadcrumbs={[
          { name: '首页', path: '/' },
          { name: '关于我们', path: '/about' },
        ]}
      />
      <main className="min-h-screen bg-background-50">
        <section className="pt-28 pb-16 px-6 md:px-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>

            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
                让每个人都能<span className="text-primary-600">创造数字化价值</span>
              </h1>
              <p className="text-lg text-foreground-600 leading-relaxed mb-8">
                无码是新一代低代码/无码开发平台，由一群来自顶尖互联网公司的工程师创立。
                我们相信，技术的价值不应用于少数人，每个人都应该有能力将自己的想法转化为数字产品。
                无码的使命是降低软件开发的门槛，让企业管理者、业务专家、创业者都能亲手搭建属于自己的数字系统。
              </p>
              <div className="flex flex-wrap gap-8">
                {[
                  { number: '100,000+', label: '活跃用户' },
                  { number: '500万+', label: '应用已创建' },
                  { number: '50+', label: '行业覆盖' },
                  { number: '99.9%', label: '服务可用性' },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="text-3xl font-heading font-bold text-primary-600">{s.number}</div>
                    <div className="text-sm text-foreground-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">发展历程</h2>
              <p className="text-foreground-600 text-base">从0到1，从1到无限可能</p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-0.5 bg-secondary-200"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((m, idx) => (
                  <div key={m.year} className={`relative flex items-start gap-6 md:gap-10 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`hidden md:block flex-1 ${idx % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                        <span className="text-xs font-bold text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">{m.year}</span>
                        <h3 className="text-lg font-heading font-semibold text-foreground-950 mt-2 mb-1">{m.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{m.description}</p>
                      </div>
                    </div>

                    <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 mt-6 z-10"></div>

                    <div className="md:hidden flex-1 ml-12 p-5 rounded-2xl bg-background-100 border border-background-200/70">
                      <span className="text-xs font-bold text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">{m.year}</span>
                      <h3 className="text-lg font-heading font-semibold text-foreground-950 mt-2 mb-1">{m.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{m.description}</p>
                    </div>

                    <div className="hidden md:block flex-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">核心团队</h2>
              <p className="text-foreground-600 text-base">汇聚行业精英，打造卓越产品</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member) => (
                <div key={member.name} className="p-7 rounded-2xl bg-background-50 border border-background-200/70 text-center hover:border-primary-200 transition-all duration-300">
                  <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-2xl font-heading font-bold mb-5">
                    {member.name.charAt(0)}
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-1">{member.name}</h3>
                  <p className="text-sm text-primary-600 font-medium mb-3">{member.role}</p>
                  <p className="text-sm text-foreground-600 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-20 md:py-28 px-6 md:px-10 bg-background-50 scroll-mt-20">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
              <p className="text-foreground-600 text-base leading-relaxed">
                无论你是想咨询产品、申请试用还是洽谈合作，我们都很乐意与你交流。
              </p>
            </div>

            <form data-readdy-form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-background-100 border border-background-200/70">
              <div className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">姓名 *</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-2">公司名称</label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的公司名称"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱 *</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-2">手机号码</label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的手机号码"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-purpose" className="block text-sm font-medium text-foreground-700 mb-2">咨询类型 *</label>
                  <select
                    id="contact-purpose"
                    name="purpose"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all appearance-none cursor-pointer"
                    defaultValue=""
                  >
                    <option value="" disabled>请选择咨询类型</option>
                    <option value="试用申请">试用申请</option>
                    <option value="产品咨询">产品咨询</option>
                    <option value="商务合作">商务合作</option>
                    <option value="技术支持">技术支持</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">留言内容 *</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请描述您的需求或问题（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字符</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  className="w-full py-3.5 rounded-full bg-primary-500 text-white font-semibold text-base hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  提交咨询
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-xl bg-accent-50 border border-accent-200/50 text-accent-700 text-sm text-center">
                    <i className="ri-check-line mr-1"></i>
                    {formMessage}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200/50 text-red-700 text-sm text-center">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formMessage}
                  </div>
                )}
              </div>
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">邮箱</h4>
                <p className="text-sm text-foreground-600">contact@bigyaooooo1.cn</p>
              </div>
              <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-3">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">电话</h4>
                <p className="text-sm text-foreground-600">400-888-9999</p>
              </div>
              <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mb-3">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">地址</h4>
                <p className="text-sm text-foreground-600">北京市海淀区中关村软件园</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}